import { ref } from 'vue';
import { IAlert } from '@/shared/interfaces/IAlert';

const hAlerts = ref<IAlert[]>([]);

/**
 * A Vue composable that provides alert management functionalities.
 *
 * @function
 * @name useHAlerts
 * @description
 * This composable provides methods to manage alerts within a Vue application.
 * It includes functionalities to check if an alert is active and to add new alerts.
 *
 * @returns {Object} An object containing the following methods:
 * - `hAlerts`: A reactive array of alert objects.
 * - `hAddAlert`: A function to add a new alert to the array.
 */
export function useHAlerts() {
  /**
   * @function
   * @name isAlertActive
   * @param {IAlert} alert - The alert object to check.
   * @returns {boolean} - Returns true if the alert is active, false otherwise.
   */
  function isAlertActive(alert: IAlert) {
    const hasTimedOut = alert.timeout ? Date.now() > (alert.addedAt ?? 0) + alert.timeout : false;

    return !alert.isClosed && !hasTimedOut;
  }

  /**
   * @function
   * @name hAddAlert
   * @param {IAlert} newAlert - The new alert object to add.
   * @returns {void}
   */
  function hAddAlert(newAlert: IAlert) {
    if (!newAlert.id) {
      newAlert.id = crypto.randomUUID();
    }

    if (!hAlerts.value.some((alert) => alert.id === newAlert.id && isAlertActive(alert))) {
      newAlert.addedAt = Date.now();

      hAlerts.value.push(newAlert);
    }
  }

  return { hAlerts, hAddAlert };
}
